import React, { useState } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { API_BASE_URL } from '../config/apiConfig';

const Login = () => {
  const [emailOrUsername, setEmailOrUsername] = useState('');
  const [password, setPassword] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const handleLogin = async (e) => {
  e.preventDefault();
  setErrorMsg('');

  const SESSION_CART_KEY = 'ccc_guest_cart';

  const safeParse = (raw, fallback) => {
    try {
      return raw ? JSON.parse(raw) : fallback;
    } catch {
      return fallback;
    }
  };

  const getGuestCart = () => safeParse(sessionStorage.getItem(SESSION_CART_KEY), []);
  const clearGuestCart = () => {
    try {
      sessionStorage.setItem(SESSION_CART_KEY, JSON.stringify([]));
    } catch {}
  };

  // map guest cart item -> backend AddToCart payload
  const toAddToCartPayload = (x, loggedInUserId) => {
    // styleOptionIds can be stored in many ways; normalize to int[]
    let styleOptionIds = [];
    if (Array.isArray(x.styleOptionIds)) styleOptionIds = x.styleOptionIds;
    else if (Array.isArray(x.styleIds)) styleOptionIds = x.styleIds;
    else if (typeof x.styleOptionIds === 'string') {
      styleOptionIds = x.styleOptionIds
        .split(',')
        .map(s => parseInt(String(s).trim(), 10))
        .filter(n => Number.isFinite(n) && n > 0);
    }

    const productId = Number(x.productId ?? x.productID ?? 0);
    const fabricId = Number(x.fabricId ?? x.fabricID ?? 0);

    return {
      userId: loggedInUserId, // ignored by backend when token exists, but ok
      productId,
      fabricId,
      styleOptionIds,

      smartSizeId: x.smartSizeId ?? null,
      bodyMeasurementId: x.bodyMeasurementId ?? null,
      shirtMeasurementId: x.shirtMeasurementId ?? null,
      isMailUsSelected: !!x.isMailUsSelected,

      standardSizeSelectionId: x.standardSizeSelectionId ?? null,

      price: Number(x.price ?? x.totalPrice ?? x.total_price ?? 0),

      isMonogram: !!x.isMonogram,
      monogramInitials: x.monogramInitials ?? '',
      monogramStyle: x.monogramStyle ?? '',
      monogramColor: x.monogramColor ?? '',
      monogramPlacement: x.monogramPlacement ?? '',

      isCollarAccentEnabled: !!x.isCollarAccentEnabled,
      isCuffAccentEnabled: !!x.isCuffAccentEnabled,
      accentFabricId: x.accentFabricId ?? null
    };
  };

  const mergeGuestCartToDb = async (token, loggedInUserId) => {
    const guestItems = getGuestCart();
    if (!Array.isArray(guestItems) || guestItems.length === 0) return;

    // Add each item into DB cart
    for (const item of guestItems) {
      const payload = toAddToCartPayload(item, loggedInUserId);

      // skip junk rows safely
      if (!payload.productId || !payload.fabricId || !payload.styleOptionIds?.length) continue;

      const res = await fetch(`${API_BASE_URL}/UserManagement/AddToCart`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      // If one item fails, continue with others (don’t block login)
      if (!res.ok) {
        const text = await res.text().catch(() => '');
        console.error('Merge AddToCart failed:', res.status, text);
      }
    }

    // clear guest cart after attempting merge
    clearGuestCart();
  };

  try {
    const response = await fetch(`${API_BASE_URL}/UserManagement/Login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ emailOrUsername, password })
    });

    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      setErrorMsg(err.message || 'Login failed');
      return;
    }

    const data = await response.json();

    // save both user and token
    localStorage.setItem('user', JSON.stringify(data.user));
    localStorage.setItem('token', data.token);

    // notify app listeners
    window.dispatchEvent(new Event('ccc_user_updated'));

    // ✅ merge browser guest cart -> DB cart
    await mergeGuestCartToDb(data.token, data.user?.userId);

    // refresh cart everywhere
    window.dispatchEvent(new Event('ccc_cart_updated'));

    window.location.href = '/';
  } catch (error) {
    setErrorMsg('Something went wrong. Please try again.');
  }
};



  return (
    <>
      <Header />
      <main className="rbt-main-wrapper">
        <section className="shirt-client-login">
          <div className="login-form-wrapper">
            <div className="login-form-box">
              <h2 className="login-form-title">Login</h2>
              <p className="login-form-subtitle">
                Access your account to track orders, manage details, and shop effortlessly.
              </p>
              <form className="login-form" onSubmit={handleLogin}>
                <input
                  type="text"
                  className="login-input"
                  placeholder="Email or Mobile Number"
                  value={emailOrUsername}
                  onChange={(e) => setEmailOrUsername(e.target.value)}
                  required
                />
                <div className="position-relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    className="login-input pe-5"
                    placeholder="Password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                  />
                  <span
                    onClick={() => setShowPassword(!showPassword)}
                    style={{
                      position: 'absolute',
                      top: '35%',
                      right: '15px',
                      transform: 'translateY(-50%)',
                      cursor: 'pointer',
                      fontSize: '14px',
                      color: '#555'
                    }}
                  >
                    {showPassword ? 'Hide' : 'Show'}
                  </span>
                </div>


                <div className="login-options">
                  <label className="remember-me">
                    <input type="checkbox" /> Remember me
                  </label>
                  <a href="/forgot-password" className="forgot-password">
                    Forgot Password?
                  </a>
                </div>

                {errorMsg && <p className="text-danger mt-2">{errorMsg}</p>}

                <button type="submit" className="login-btn">
                  Login
                </button>
              </form>
              <p className="register-cta">
                New here? <a href="/register">Create an account</a>
              </p>
            </div>
          </div>
        </section>

        <Footer />
      </main>
      <div className="rbt-progress-parent">
        <svg className="rbt-back-circle svg-inner" width="100%" height="100%" viewBox="-1 -1 102 102">
          <path d="M50,1 a49,49 0 0,1 0,98 a49,49 0 0,1 0,-98" />
        </svg>
      </div>
    </>
  );
};

export default Login;
